import { Link, useNavigate, useParams } from "react-router-dom";
import {
    useCurrentShipment,
    useCurrentTissue,
    useShippingMethodSelectOptions,
    // useShippingStatusSelectOptions,
} from "../../hooks";
import { useAccountQuery, useShipmentQuery } from "../../queries";
import {
    DateInput,
    Input,
    LoadingSpinner,
    ReadonlyGroup,
    Select,
    StatefulForm,
    Textarea,
    useStatefulForm,
} from "../../component-library";
import { z } from "zod";
import { useMemo, useState } from "react";
import { Claims, CreateShipmentRequest, QueryType, Shipment } from "../../models";
import { TissueHeader } from "../tissues";
import { Button, Col, Modal, Row } from "react-bootstrap";
import { usePermissions, useToasts } from "../../contexts";
import { createPatch } from "rfc6902";

const ShipmentSchema = z.object({
    shippingStatusId: z.coerce
        .number()
        .transform((val) => (val ? Number(val) : undefined))
        .optional(),
    shippingMethodId: z.coerce
        .number()
        .transform((val) => (val ? Number(val) : undefined))
        .optional(),
    fromAccountId: z.coerce.number().transform((val) => (val ? Number(val) : undefined)),
    toAccountId: z.coerce.number().transform((val) => (val ? Number(val) : undefined)),
    countryId: z.coerce
        .number()
        .transform((val) => (val ? Number(val) : undefined))
        .optional(),
    estimatedArrival: z.string().optional(),
    trackingNumber: z.string().optional(),
    comments: z.string().optional(),
});

export type ShipmentFormValues = z.infer<typeof ShipmentSchema>;

interface ShipmentFormProps {
    isTissue?: boolean;
}

export function ShipmentForm({ isTissue }: Readonly<ShipmentFormProps>) {
    const navigate = useNavigate();
    const { shipmentId, listSlug } = useParams<{ shipmentId: string; listSlug: string }>();
    const { hasPermission } = usePermissions();
    const { tissue } = useCurrentTissue();
    const isEditing = !!shipmentId && shipmentId !== "new";
    const canEdit =
        (isEditing && hasPermission(Claims.EditShipment)) || (!isEditing && hasPermission(Claims.CreateShipment));
    const { errorToast } = useToasts();
    const { createMutation, updateMutation, deleteMutation } = useShipmentQuery({
        id: isEditing ? shipmentId : undefined,
    });
    const {
        shipment,
        isFetching: isShipmentFetching,
        createdByName,
        packedByName,
        receivedByName,
        createdOn,
        shippedOn,
        arrivedOn,
    } = useCurrentShipment();
    const shippingMethodOptions = useShippingMethodSelectOptions();
    // const shippingStatusOptions = useShippingStatusSelectOptions();
    const {
        listQuery: { data: accounts, isFetching: isAccountsFetching },
    } = useAccountQuery({ queryType: QueryType.List });

    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const accountOptions = useMemo(
        () => accounts?.map((account) => ({ label: account.name, value: account.id })) ?? [],
        [accounts]
    );

    const defaultValues = useMemo(
        () => ({
            shippingStatusId: shipment?.shippingStatusId ?? "",
            shippingMethodId: shipment?.shippingMethodId ?? "",
            fromAccountId: shipment?.fromAccountId ?? "",
            toAccountId: shipment?.toAccountId ?? "",
            estimatedArrival: shipment?.estimatedArrival ?? "",
            trackingNumber: shipment?.trackingNumber ?? "",
            comments: shipment?.comments ?? "",
        }),
        [shipment]
    );

    const create = async (values: ShipmentFormValues) => {
        if (!tissue || !hasPermission(Claims.CreateShipment)) return;
        const request: CreateShipmentRequest = {
            tissueIds: [tissue.id],
            shipment: {
                ...values,
            },
        };
        await createMutation.mutateAsync(request);
    };

    const edit = async (values: ShipmentFormValues) => {
        if (!shipment || !hasPermission(Claims.EditShipment)) return;
        const updatedShipment: Shipment = {
            ...shipment,
            ...values,
        };
        const patch = createPatch(shipment, updatedShipment);
        await updateMutation.mutateAsync(patch);
    };

    const handleSubmit = async (values: ShipmentFormValues) => {
        try {
            if (isEditing) {
                await edit(values);
            } else {
                await create(values);
            }
            navigate(-1);
        } catch (error) {
            errorToast(error);
        }
    };

    const { methods, isDirty } = useStatefulForm({ defaultValues, schema: ShipmentSchema });

    const handleDelete = async () => {
        if (!shipment?.id || !hasPermission(Claims.DeleteShipment)) return;
        try {
            await deleteMutation.mutateAsync(shipment.id);
            navigate(-1);
        } catch (error) {
            errorToast(error);
        }
    };

    if (isShipmentFetching) return <LoadingSpinner />;

    return (
        <>
            {isTissue && <TissueHeader />}
            <div className={isTissue ? "tissue-detail-content" : "case-detail-content"}>
                {isTissue ? (
                    <h1>
                        {isEditing ? "Edit" : "New"} Shipment for Tissue #{tissue?.number}
                    </h1>
                ) : (
                    <h1>Edit Shipment {shipment?.orderId ? `for Order #${shipment.orderId}` : ""}</h1>
                )}
                {!!shipment?.orderId && (
                    <>
                        {isDirty ? (
                            <Button size="sm" variant="info" className="mb-2" disabled>
                                Save Changes to View Order
                            </Button>
                        ) : (
                            <Link
                                to={`/lists/${listSlug}/orders/${shipment.orderId}`}
                                className="btn btn-sm btn-info mb-2"
                            >
                                View Order
                            </Link>
                        )}
                    </>
                )}

                <StatefulForm
                    methods={methods}
                    onSubmit={(values) => handleSubmit(values as ShipmentFormValues)}
                    readonly={!canEdit}
                >
                    <Row>
                        {/* <Col lg={3} md={6} className="mb-2">
                            <Select label="Status" accessor="shippingStatusId" options={shippingStatusOptions} />
                        </Col> */}
                        <Col lg={3} md={6} className="mb-2">
                            <Select
                                label="Method"
                                accessor="shippingMethodId"
                                options={shippingMethodOptions}
                                isDisabled={!canEdit}
                            />
                        </Col>
                        <Col lg={3} md={6} className="mb-2">
                            {isAccountsFetching ? (
                                <>Loading accounts...</>
                            ) : (
                                <Select
                                    label="From"
                                    accessor="fromAccountId"
                                    options={accountOptions}
                                    required
                                    isDisabled={!canEdit}
                                />
                            )}
                        </Col>
                        <Col lg={3} md={6} className="mb-2">
                            {isAccountsFetching ? (
                                <>Loading accounts...</>
                            ) : (
                                <Select
                                    label="To"
                                    accessor="toAccountId"
                                    options={accountOptions}
                                    required
                                    isDisabled={!canEdit}
                                />
                            )}
                        </Col>
                        <Col lg={3} className="mb-2">
                            <DateInput
                                label="Estimated Arrival"
                                accessor="estimatedArrival"
                                withTime
                                disabled={!canEdit}
                            />
                        </Col>
                        <Col lg={6} className="mb-2">
                            <Input label="Tracking Number" accessor="trackingNumber" />
                        </Col>
                        <Col lg={6} className="mb-2">
                            <Textarea label="Comments" accessor="comments" />
                        </Col>
                        <Col lg={3} className="mb-2">
                            <ReadonlyGroup label="Created By" value={createdByName} />
                        </Col>
                        <Col lg={3} className="mb-2">
                            <ReadonlyGroup label="Created On" value={createdOn} />
                        </Col>
                        <Col lg={3} className="mb-2">
                            <ReadonlyGroup label="Packed By" value={packedByName} />
                        </Col>
                        <Col lg={3} className="mb-2">
                            <ReadonlyGroup label="Shipped On" value={shippedOn} />
                        </Col>
                        <Col lg={3} className="mb-2">
                            <ReadonlyGroup label="Received By" value={receivedByName} />
                        </Col>
                        <Col lg={3} className="mb-2">
                            <ReadonlyGroup label="Received On" value={arrivedOn} />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Button variant="secondary" onClick={() => navigate(-1)} className="me-2">
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={!isDirty || updateMutation.isLoading || !canEdit}
                                className="me-2"
                            >
                                {updateMutation.isLoading ? "Saving..." : "Save"}
                            </Button>
                            {isEditing && hasPermission(Claims.DeleteShipment) && !shipment?.orderId && (
                                <Button
                                    variant="danger"
                                    onClick={() => setShowDeleteModal(true)}
                                    disabled={deleteMutation.isLoading}
                                >
                                    {deleteMutation.isLoading ? "Deleting..." : "Delete"}
                                </Button>
                            )}
                        </Col>
                    </Row>
                </StatefulForm>

                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton>Delete Shipment</Modal.Header>
                    <Modal.Body>Are you sure you want to delete this shipment?</Modal.Body>
                    <Modal.Footer className="d-flex justify-content-between">
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleDelete} disabled={deleteMutation.isLoading}>
                            {deleteMutation.isLoading ? "Deleting..." : "Delete"}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </>
    );
}
